import {
  CustomerBankAccountMandateStateEnumType,
  Maybe,
  B2BPaymentMethod,
  PaymentMethodState,
  PaymentMethodStripeSepa,
} from "@oysterhr/schema"
import { Link } from "@oysterhr/ui"
import { useTranslation } from "react-i18next"
import { useCallback, useMemo } from "react"
import { MandateSign } from "./useMandateSign"

type OnAuthorizeFunction = (
  paymentMethod?: Maybe<PaymentMethodStripeSepa | B2BPaymentMethod>,
) => void

export const useBankAccountPendingActions = (
  onAuthorize: OnAuthorizeFunction,
  onResend: () => void,
  setShowConfirmSharingModal: (show: boolean) => void,
  companyId: string,
  mandateSign: MandateSign,
  customerBankAccount?: Maybe<B2BPaymentMethod>,
) => {
  const { t } = useTranslation("company_interface/CompanyPaymentMethodsPage/LandingPage")
  const { onMandateSign, isSigning } = mandateSign

  const onSignMandate = useCallback(() => {
    // We don't show generation toast here
    onMandateSign(companyId, false)
  }, [onMandateSign, companyId])

  const onLodgeMandate = useCallback(() => {
    setShowConfirmSharingModal(true)
  }, [setShowConfirmSharingModal])

  const actions = useMemo(() => {
    const mandateState = customerBankAccount?.mandate?.state
    const isAuthorizedByUser = customerBankAccount?.state === PaymentMethodState.AuthorizedByUser

    const isMandateSignPending =
      mandateState === CustomerBankAccountMandateStateEnumType.Initialized
    const canSignMandate = isAuthorizedByUser && isMandateSignPending

    const isLodgePending =
      mandateState &&
      [
        CustomerBankAccountMandateStateEnumType.Initialized,
        CustomerBankAccountMandateStateEnumType.Signed,
      ].includes(mandateState)
    const canLodgeMandate = mandateState === CustomerBankAccountMandateStateEnumType.Signed

    const ADD_PAYMENT = { step: 1, taskName: t("tasks.addPayment.taskName") }
    const AUTHORIZE_PAYMENT_METHOD = {
      step: 2,
      taskName: t("tasks.authorization.taskName"),
      action: (
        <Link as="button" onClick={() => onAuthorize(customerBankAccount)}>
          {t("tasks.authorization.action")}
        </Link>
      ),
    }
    const SIGN_MANDATE_STEP = {
      step: 3,
      taskName: t("tasks.signMandate.taskName"),
      loading: isSigning,
      action: customerBankAccount?.userNotSigner ? (
        <Link as="button" disabled={!isAuthorizedByUser} onClick={onResend}>
          {t("tasks.resendEmail.action")}
        </Link>
      ) : (
        <Link as="button" disabled={!canSignMandate || isSigning} onClick={onSignMandate}>
          {t("tasks.signMandate.action")}
        </Link>
      ),
    }
    const CONFIRM_MANDATE_SHARING_STEP = {
      step: 4,
      taskName: t("tasks.confirmSharing.taskName"),
      action: (
        <Link as="button" disabled={!canLodgeMandate} onClick={onLodgeMandate}>
          {t("tasks.confirmSharing.action")}
        </Link>
      ),
    }
    const VERIFY_PAYMENT_METHOD_STEP = {
      step: 5,
      taskName: t("tasks.verification.taskName"),
    }

    const pendingActions = []
    const completedActions = [ADD_PAYMENT]

    // Payment authorization
    if (customerBankAccount?.state === PaymentMethodState.Initialized) {
      pendingActions.push(AUTHORIZE_PAYMENT_METHOD)
    } else {
      completedActions.push(AUTHORIZE_PAYMENT_METHOD)
    }

    if (isMandateSignPending) {
      pendingActions.push(SIGN_MANDATE_STEP)
    } else {
      completedActions.push(SIGN_MANDATE_STEP)
    }

    if (isLodgePending) {
      pendingActions.push(CONFIRM_MANDATE_SHARING_STEP)
    } else {
      completedActions.push(CONFIRM_MANDATE_SHARING_STEP)
    }

    pendingActions.push(VERIFY_PAYMENT_METHOD_STEP)

    return { pendingActions, completedActions }
  }, [customerBankAccount, isSigning, onAuthorize, onResend, onSignMandate, onLodgeMandate, t])

  return actions
}
